// The Analyses table (DESIGN_SPEC §10.8, §11.3; RESTYLE §5): `.t.t--select` with UTC day groups, newest first, child
// runs under their parent (family.ts).
//   <JobsTable groups selected stale query queue offered names onSelect onOpen onFold onFocusRow register observe />
//     groups    day groups of family items (arrange)                queue    id → place among queued runs
//     offered   profiles some node offers (null: fleet unknown)      names    id → sample name (parents of child rows)
//     stale     hold the frame dimmed (.is-stale) while a larger page loads or a refresh failed
// Layout: from 900 px the table is fixed-width (sample and signal share what the fixed columns leave, and
// truncate) and its header sticks under the nav; below 900 px it scrolls sideways inside .tscroll with the
// verdict column pinned. Node shows from 1280 px; environment hides on phones. The verdict banner carries the score.
import { Fragment, useSyncExternalStore } from "react";
import { nOf } from "@/lib/format";
import type { TreeGroup } from "./family";
import { JobRow, rowId, STICKY } from "./JobRow";

type JobsTableProps = {
  groups: readonly TreeGroup[];
  selected: string | null;
  stale?: boolean;
  query: string;
  queue: ReadonlyMap<string, number>;
  offered: ReadonlySet<string> | null;
  names: ReadonlyMap<string, string>;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  onFold: (id: string, fold: boolean) => void;
  onFocusRow: (id: string) => void;
  register: (id: string, el: HTMLTableRowElement | null) => void;
  observe: (el: HTMLElement, id: string) => () => void;
};

const HEAD = "min-[900px]:top-nav";
const cx = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(" ");

function useMinWidth(px: number): boolean {
  const query = `(min-width: ${px}px)`;
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(query);
      m.addEventListener("change", cb);
      return () => m.removeEventListener("change", cb);
    },
    () => window.matchMedia(query).matches,
    () => true,
  );
}

export function JobsTable({ groups, selected, stale, query, queue, offered, names, onSelect, onOpen, onFold, onFocusRow, register, observe }: JobsTableProps) {
  // a day row must span exactly the visible columns: a wider span adds a phantom column that takes space
  const showEnv = useMinWidth(720);
  const showSignature = useMinWidth(1200);
  const showTags = useMinWidth(1360);
  const span = 5 + Number(showEnv) + Number(showSignature) + Number(showTags);
  return (
    // relative: absolutely positioned descendants (sr-only words) stay inside the scroller instead of widening the page
    <div className={cx("tscroll relative min-[900px]:overflow-visible", stale && "is-stale")}>
      <table className="t t--select min-[900px]:table-fixed" aria-label="Analyses, newest first; child runs under their parent">
        <thead>
          <tr>
            <th scope="col" className={cx(HEAD, STICKY, "min-[900px]:w-[152px]")}>
              verdict · state
            </th>
            <th scope="col" className={cx(HEAD, "min-[1360px]:w-[26%]")}>
              sample
            </th>
            <th scope="col" className={HEAD}>
              strongest signal
            </th>
            <th scope="col" className={cx(HEAD, "w-[132px] max-[1199px]:hidden")}>
              signature
            </th>
            <th scope="col" className={cx(HEAD, "w-[160px] max-[1359px]:hidden")}>
              tags
            </th>
            <th scope="col" className={cx(HEAD, "w-[148px] max-[1279px]:w-[104px] max-[719px]:hidden")}>
              environment
            </th>
            <th scope="col" className={cx(HEAD, "r w-[88px]")}>
              ran
            </th>
            <th scope="col" className={cx(HEAD, "r w-[128px] pr-2.5")} aria-sort="descending">
              submitted <span aria-hidden="true">↓</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <Fragment key={g.key}>
              <tr className="day">
                <td colSpan={span}>
                  {/* the label itself sticks: a cell as wide as the table cannot */}
                  <div className="sticky left-2.5 w-max">
                    <span className="d">{g.label}</span>
                    <span className="n">{nOf(g.count, "run")}</span>
                  </div>
                </td>
              </tr>
              {g.items.map((it) => {
                const e = it.eff;
                const parent = it.under ?? it.orphanOf;
                return (
                  <JobRow
                    key={e.id}
                    eff={e}
                    selected={selected === e.id}
                    queuePos={queue.get(e.id)}
                    profileOffered={offered ? offered.has(e.job.profile) : undefined}
                    parentName={parent ? names.get(parent) : undefined}
                    query={query}
                    depth={it.depth}
                    last={it.last}
                    rails={it.rails}
                    kids={it.kids}
                    shownKids={it.shownKids}
                    liveKids={it.liveKids}
                    maliciousKids={it.maliciousKids}
                    folded={it.folded}
                    childIds={it.childIds.map(rowId).join(" ")}
                    under={it.under}
                    orphanOf={it.orphanOf}
                    onSelect={onSelect}
                    onOpen={onOpen}
                    onFold={onFold}
                    onFocusRow={onFocusRow}
                    register={register}
                    observe={observe}
                  />
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
